import { motion } from "framer-motion";
import { ArrowUpRight, Globe2, BadgeCheck, Award } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const AHLEI_LINK = "https://ahlei.servsafebrands.com/international-overview/india";

export default function Accreditation() {
  return (
    <section
      id="accreditation"
      data-testid="accreditation-section"
      className="border-b border-brand-line bg-brand-surface py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-4"
        >
          <div className="border border-brand-line bg-white p-10 text-center shadow-sm">
            <img
              data-testid="ahlei-logo"
              src="/images/ahlei-logo.png"
              alt="AHLEI — American Hotel & Lodging Educational Institute, USA"
              className="mx-auto w-full max-w-[240px]"
            />
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-muted">
              Permanent Licensed Partner · USA
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="lg:col-span-8"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-terra">
            International Accreditation
          </p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
            An American-standard curriculum, <em className="italic text-brand-terra">licensed permanently</em>.
          </h2>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-brand-muted md:text-lg">
            Abhi Institute of Hotel Management holds a{" "}
            <strong className="font-semibold text-brand-ink">
              permanent licence from AHLEI — the American Hotel &amp; Lodging Educational Institute, USA
            </strong>
            . This international collaboration means every course at ABHI follows
            AHLEI's globally benchmarked hospitality curriculum — the same
            standard taught at leading hotel schools worldwide — so our students
            graduate ready for hotels in India and abroad.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-green">
              <Globe2 className="h-4 w-4 text-brand-terra" />
              Officially listed AHLEI India licensee
            </span>
            <a
              data-testid="ahlei-verify-link"
              href={AHLEI_LINK}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-brand-terra underline decoration-brand-terra/30 underline-offset-4 transition-colors hover:decoration-brand-terra"
            >
              Verify on AHLEI's official website
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            <div
              data-testid="credential-ugc"
              className="flex items-center gap-4 rounded-md border border-brand-green/25 bg-brand-green/5 p-5"
            >
              <BadgeCheck className="h-8 w-8 shrink-0 text-brand-green" />
              <div>
                <p className="font-serif text-lg leading-tight text-brand-ink">UGC Approved Courses</p>
                <p className="mt-1 text-xs leading-relaxed text-brand-muted">
                  Every program approved by the University Grants Commission
                </p>
              </div>
            </div>
            <div
              data-testid="credential-naac"
              className="flex items-center gap-4 rounded-md border border-brand-green/25 bg-brand-green/5 p-5"
            >
              <Award className="h-8 w-8 shrink-0 text-brand-green" />
              <div>
                <p className="font-serif text-lg leading-tight text-brand-ink">NAAC Accredited Courses</p>
                <p className="mt-1 text-xs leading-relaxed text-brand-muted">
                  Courses accredited by the National Assessment and Accreditation Council
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
